'use client';

import { c as _c } from "react/compiler-runtime";
import { ViewDetailsButton } from '@/comps/view-details-button';
import { PaginatedTableLayout } from '@/comps/table/PaginatedTableLayout';
import { REPORT_DATATABLE_TABLE_CLASSNAME } from '@/comps/table/constants';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/comps/primitives/table';
import { AiProficiencyReportCard } from '@/domains/pages/ai-proficiency/components/ai-proficiency-report-card';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { useModelSavingsTasksQuery } from '@/domains/pages/model-savings/hooks/use-model-savings-tasks-query';
import { SavingsShareProgress } from '@/domains/pages/model-savings/components/savings-share-progress';
import { ModelMixLegend } from '@/domains/pages/model-savings/components/model-mix-bars';
import { ModelMixPie, ModelMixPieSkeleton } from '@/domains/pages/model-savings/components/model-mix-pie';
import { MODEL_SAVINGS_POSITIVE_COLOR, SAVINGS_TASK_TABLE_TOOLTIPS } from '@/domains/pages/model-savings/constants';
import type { SavingsTask } from '@/domains/pages/model-savings/types';
import type { ModelSavingsTasksQueryScope } from '@/domains/pages/model-savings/utils/model-savings-api';
import { formatAnnualSavings, formatAnnualSpend } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { cn } from '@/utils/shad';
import { Pagination, usePagination, DEFAULT_PAGE_SIZE } from '@/comps/pagination';
import { InfoTooltip } from '@/comps/info-tooltip';
import { Skeleton } from '@/comps/primitives/skeleton';
import { useEffect, useRef, type ReactNode } from 'react';
import { humanizeString } from '@/utils/string.utils';
const MODEL_MIX_COLUMN_WIDTH_PX = 200;

/** Header label plus the same (i) affordance used next to card titles. */
function TaskTableHead(t0) {
  const $ = _c(12);
  const {
    title,
    tooltip,
    className,
    trailing
  } = t0;
  const t1 = `${title} column description`;
  let t2;
  if ($[0] !== t1 || $[1] !== tooltip) {
    t2 = <InfoTooltip content={tooltip} label={t1} />;
    $[0] = t1;
    $[1] = tooltip;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2 || $[4] !== title) {
    t3 = <span className="flex items-center">{title}{t2}</span>;
    $[3] = t2;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3 || $[7] !== trailing) {
    t4 = <div className="flex items-center justify-between gap-3">{t3}{trailing}</div>;
    $[6] = t3;
    $[7] = trailing;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== className || $[10] !== t4) {
    t5 = <TableHead className={className}>{t4}</TableHead>;
    $[9] = className;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function serializeTasksForSync(tasks: SavingsTask[]): string {
  return tasks.map(task => `${task.id}:${task.savingAnnual}:${task.spendAnnual}:${task.actualMix.map(segment => segment.percent).join('-')}:${task.optimalMix.map(segment => segment.percent).join('-')}`).join('|');
}
const TABLE_LOADING_ROW_COUNT = 8;
function SavingsTaskTableLoadingRow() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <TableCell className="pl-8"><Skeleton className="h-4 w-3/4 max-w-52" /></TableCell>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TableCell><div className="flex items-center gap-2"><Skeleton className="h-4 w-16" /><Skeleton className="size-4 rounded-full" /></div></TableCell>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TableCell><Skeleton className="h-4 w-20" /></TableCell>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <TableCell style={{
      width: MODEL_MIX_COLUMN_WIDTH_PX,
      minWidth: MODEL_MIX_COLUMN_WIDTH_PX
    }}><ModelMixPieSkeleton /></TableCell>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TableCell style={{
      width: MODEL_MIX_COLUMN_WIDTH_PX,
      minWidth: MODEL_MIX_COLUMN_WIDTH_PX
    }}><ModelMixPieSkeleton /></TableCell>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TableRow>{t0}{t1}{t2}{t3}{t4}<TableCell className="pr-6 text-right"><Skeleton className="ml-auto h-7 w-20 rounded-full" /></TableCell></TableRow>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
type SavingsTaskTableProps = {
  queryScope: ModelSavingsTasksQueryScope;
  onTaskClick: (taskId: string) => void;
  onTasksLoaded?: (tasks: SavingsTask[]) => void;
};
export function SavingsTaskTable(t0) {
  const $ = _c(32);
  const {
    queryScope,
    onTaskClick,
    onTasksLoaded
  } = t0;
  const {
    reset,
    offset,
    pageSize,
    ...pagination
  } = usePagination(DEFAULT_PAGE_SIZE);
  const t1 = queryScope.projectId;
  let t2;
  if ($[0] !== queryScope.scopeFilters) {
    t2 = JSON.stringify(queryScope.scopeFilters);
    $[0] = queryScope.scopeFilters;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const scopeKey = `${t1}:${t2}`;
  const syncedTasksKeyRef = useRef(null);
  let t3;
  if ($[2] !== reset) {
    t3 = () => {
      reset();
      syncedTasksKeyRef.current = null;
    };
    $[2] = reset;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== reset || $[5] !== scopeKey) {
    t4 = [reset, scopeKey];
    $[4] = reset;
    $[5] = scopeKey;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== offset || $[8] !== pageSize || $[9] !== queryScope) {
    t5 = {
      ...queryScope,
      enabled: true,
      limit: pageSize,
      offset,
      queryKeySuffix: "table"
    };
    $[7] = offset;
    $[8] = pageSize;
    $[9] = queryScope;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    tasks,
    total,
    isLoading,
    isFetching
  } = useModelSavingsTasksQuery(t5);
  let t6;
  let t7;
  if ($[11] !== onTasksLoaded || $[12] !== tasks) {
    t6 = () => {
      if (!onTasksLoaded) {
        return;
      }
      const nextKey = serializeTasksForSync(tasks);
      if (syncedTasksKeyRef.current === nextKey) {
        return;
      }
      syncedTasksKeyRef.current = nextKey;
      onTasksLoaded(tasks);
    };
    t7 = [onTasksLoaded, tasks];
    $[11] = onTasksLoaded;
    $[12] = tasks;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  const totalPages = pagination.calcTotalPages(total);
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ModelMixLegend />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = isLoading || isFetching;
  let t10;
  if ($[16] !== pageSize || $[17] !== pagination || $[18] !== t9 || $[19] !== totalPages) {
    t10 = <div className="flex h-14 items-center justify-end border-t border-t-lightborder bg-neutral-950 px-8 py-3"><Pagination {...pagination} pageSize={pageSize} totalPages={totalPages} flush={true} variant="simple" loading={t9} /></div>;
    $[16] = pageSize;
    $[17] = pagination;
    $[18] = t9;
    $[19] = totalPages;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <colgroup><col /><col className="w-40" /><col className="w-40" /><col style={{
        width: MODEL_MIX_COLUMN_WIDTH_PX,
        minWidth: MODEL_MIX_COLUMN_WIDTH_PX
      }} /><col style={{
        width: MODEL_MIX_COLUMN_WIDTH_PX,
        minWidth: MODEL_MIX_COLUMN_WIDTH_PX
      }} /><col className="w-40" /></colgroup>;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TableHeader className="sticky top-0 z-0 bg-neutral-950"><TableRow className="border-t"><TaskTableHead title="Task" tooltip={SAVINGS_TASK_TABLE_TOOLTIPS.task} className="pl-8" /><TaskTableHead title="Saving / yr" tooltip={SAVINGS_TASK_TABLE_TOOLTIPS.saving} /><TaskTableHead title="Spend / yr" tooltip={SAVINGS_TASK_TABLE_TOOLTIPS.spend} /><TaskTableHead title="Current mix" tooltip={SAVINGS_TASK_TABLE_TOOLTIPS.currentMix} /><TaskTableHead title="Optimal mix" tooltip={SAVINGS_TASK_TABLE_TOOLTIPS.optimalMix} /><TableHead className="pr-6 text-right"> </TableHead></TableRow></TableHeader>;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== isLoading || $[24] !== onTaskClick || $[25] !== tasks) {
    t13 = isLoading && tasks.length === 0 ? Array.from({
      length: TABLE_LOADING_ROW_COUNT
    }).map(_temp) : tasks.map(task => <TableRow key={task.id} className="cursor-pointer hover:bg-muted/20" onClick={() => onTaskClick(task.id)}><TableCell className="pl-8 font-medium">{humanizeString(task.name)}</TableCell><TableCell><div className="flex items-center gap-2"><span className={cn("font-semibold", AIFI_DATA_NUMBER_CLASS)} style={{
            color: MODEL_SAVINGS_POSITIVE_COLOR
          }}>{formatAnnualSavings(task.savingAnnual)}</span><div className="flex items-center gap-1"><SavingsShareProgress value={task.savingSharePercent} size={16} /></div></div></TableCell><TableCell className={AIFI_DATA_NUMBER_CLASS}>{formatAnnualSpend(task.spendAnnual)}</TableCell><TableCell style={{
        width: MODEL_MIX_COLUMN_WIDTH_PX,
        minWidth: MODEL_MIX_COLUMN_WIDTH_PX
      }}><ModelMixPie mix={task.actualMix} label="Current mix" calculationRows={task.calculationRows} spendField="actualSpend" /></TableCell><TableCell style={{
        width: MODEL_MIX_COLUMN_WIDTH_PX,
        minWidth: MODEL_MIX_COLUMN_WIDTH_PX
      }}><ModelMixPie mix={task.optimalMix} label="Optimal mix" calculationRows={task.calculationRows} spendField="optimalSpend" /></TableCell><TableCell className="pr-6 text-right"><ViewDetailsButton onClick={event => {
          (event as React.MouseEvent).stopPropagation();
          onTaskClick(task.id);
        }} /></TableCell></TableRow>);
    $[23] = isLoading;
    $[24] = onTaskClick;
    $[25] = tasks;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <div className="overflow-x-auto"><Table className={REPORT_DATATABLE_TABLE_CLASSNAME}>{t11}{t12}<TableBody striped={true}>{t13}</TableBody></Table></div>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t14) {
    t15 = <AiProficiencyReportCard title="Savings opportunity by task" description="Ranked by annualized savings if each task used the most cost-efficient model tier." contentClassName="flex min-h-0 flex-1 flex-col p-0!" headerTrailing={t8}><PaginatedTableLayout className="min-h-0 flex-1" footer={t10}>{t14}</PaginatedTableLayout></AiProficiencyReportCard>;
    $[29] = t10;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
}
function _temp(_, index) {
  return <SavingsTaskTableLoadingRow key={index} />;
}